import { useCallback, useEffect, useState } from 'react'
import { Login } from '@/components/Login'
import { Terminal } from '@/components/Terminal'
import { clearSession, logout, restore, type SessionUser } from '@/lib/auth'
import { api } from '@/lib/api'
import { useStore } from '@/store'

export default function App() {
  const setHealth = useStore((s) => s.setHealth)
  const setStoreSession = useStore((s) => s.setSession)
  const clearStoreSession = useStore((s) => s.clearSession)
  const [user, setUser] = useState<SessionUser | null>(null)
  const [restoring, setRestoring] = useState(true)

  // The terminal's own store is what panels consult for can('order:create')
  // etc. — keep it in lockstep with the auth session.
  const adoptUser = useCallback((u: SessionUser) => {
    setStoreSession({ userId: u.userId, roles: u.roles, permissions: u.permissions })
    setUser(u)
  }, [setStoreSession])

  const endSession = useCallback(() => {
    clearSession()
    clearStoreSession()
    setUser(null)
  }, [clearStoreSession])

  // Try to resume the previous session; until that settles, render nothing
  // rather than flashing the login page at a signed-in trader.
  useEffect(() => {
    let cancelled = false
    void restore(endSession).then((u) => {
      if (!cancelled) {
        if (u) adoptUser(u)
        setRestoring(false)
      }
    })
    return () => { cancelled = true }
  }, [endSession, adoptUser])

  // Any API 401 ends the session — a half-authenticated terminal is worse
  // than a login page.
  useEffect(() => {
    const onUnauthorized = () => endSession()
    window.addEventListener('qorvex:unauthorized', onUnauthorized)
    return () => window.removeEventListener('qorvex:unauthorized', onUnauthorized)
  }, [endSession])

  useEffect(() => {
    if (!user) return
    let cancelled = false

    // Poll health rather than assume it. A terminal that shows a green dot for
    // a dead engine is worse than one that shows nothing.
    const poll = async () => {
      try {
        await api.health()
        if (!cancelled) setHealth({ api: 'connected', lastUpdate: Date.now() })
      } catch {
        if (!cancelled) setHealth({ api: 'disconnected' })
      }
      try {
        const s = await api.engineStatus()
        if (!cancelled) {
          setHealth({ engine: (s as { ok?: boolean }).ok ? 'healthy' : 'unavailable' })
        }
      } catch {
        if (!cancelled) setHealth({ engine: 'unavailable' })
      }
    }
    void poll()
    const timer = setInterval(poll, 5_000)
    return () => { cancelled = true; clearInterval(timer) }
  }, [setHealth, user])

  if (restoring) return null
  if (!user) {
    return (
      <Login
        title="Trader sign-in"
        subtitle="Qorvex trading terminal"
        onSuccess={adoptUser}
        onExpired={endSession}
      />
    )
  }

  // The identity chip lives IN the header, not floating over the status bar.
  // A fixed-position overlay sat on top of the P&L and the kill switch — the
  // two controls that must never be obscured.
  return (
    <Terminal
      user={user}
      onSignOut={() => { void logout().then(() => { clearStoreSession(); setUser(null) }) }}
    />
  )
}
